import React, { useState } from 'react';
import { Box, Stack, Button, Paper, Typography } from '@mui/material';
import ModuleStep from './ModuleStep';
import CategoryStep from './CategoryStep';

// Stepper labels
const steps = [
  'Patient',
  'Module',
  'Category',
  'Type',
  'Section'
];

// Patient types
const patientTypes = [
  'Male Patient',
  'Female Patient',
  'Donor Patient',
  'Surrogate Patient'
];

const TemplateModifier: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedPatient, setSelectedPatient] = useState<string>('Female Patient');
  const [selectedModule, setSelectedModule] = useState<string>('Clinical');
  const [selectedCategory, setSelectedCategory] = useState<string>('Profile');

  // Custom Stepper UI matching the image
  const renderStepper = () => (
    <Paper
      elevation={2}
      sx={{
        borderRadius: 3,
        p: 1.5,
        mb: 3,
        width: 700,
        display: 'flex',
        alignItems: 'center',
        background: '#fff',
      }}
    >
      {steps.map((label, idx) => (
        <React.Fragment key={label}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: idx === activeStep ? '#FFEBE6' : '#BDBDBD',
                color: idx === activeStep ? '#FF6F61' : '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 600,
                fontSize: 16,
                transition: 'background 0.2s',
              }}
            >
              {idx + 1}
            </Box>
            <Typography
              sx={{
                color: idx === activeStep ? '#FF6F61' : '#757575',
                fontWeight: idx === activeStep ? 600 : 500,
                fontSize: 16,
                ml: 0.5,
                mr: 0, // No right margin for last step
                transition: 'color 0.2s',
              }}
            >
              {label}
            </Typography>
          </Box>
          {idx < steps.length - 1 && (
            <Box
              sx={{
                width: 48, // fixed width instead of flex
                height: 2,
                bgcolor: '#E0E0E0',
                mx: 1,
                borderRadius: 1,
              }}
            />
          )}
        </React.Fragment>
      ))}
    </Paper>
  );

  // Patient selection UI using MUI
  const renderPatientSelection = () => (
    <Box>
      <Typography variant="h6" fontWeight={500} mb={2}>
        Select Patient :
      </Typography>
      <Stack direction="row" spacing={3}>
        {patientTypes.map(type => (
          <Button
            key={type}
            variant={selectedPatient === type ? 'contained' : 'outlined'}
            color={selectedPatient === type ? 'error' : 'inherit'}
            sx={{
              bgcolor: selectedPatient === type ? '#FFEBE6' : '#F5F5F5',
              color: selectedPatient === type ? '#FF6F61' : '#888',
              minWidth: 180,
              height: 56,
              fontSize: 18,
              fontWeight: selectedPatient === type ? 600 : 400,
              borderRadius: 2,
              boxShadow: 'none',
              border: 'none',
              '&:hover': {
                bgcolor: selectedPatient === type ? '#FFEBE6' : '#e0e0e0'
              }
            }}
            onClick={() => setSelectedPatient(type)}
          >
            {type}
          </Button>
        ))}
      </Stack>
    </Box>
  );

  // Navigation buttons using MUI
  const renderNavigation = () => (
    <Box
      sx={{
        position: 'fixed',
        bottom: 32,
        right: 32,
        display: 'flex',
        gap: 2
      }}
    >
      <Button
        variant="outlined"
        color="inherit" 
        sx={{
          backgroundColor: '#fff',
          color: '#444',
          borderRadius: 2,
          minWidth: 120,
          height: 48,
          fontWeight: 500,
          fontSize: 16,
          border: '1px solid #E0E0E0'
        }}
        disabled={activeStep === 0}
        onClick={() => setActiveStep(s => Math.max(0, s - 1))}
      >
        Back
      </Button>
      <Button
        variant="contained"
        color="inherit"
        sx={{
          backgroundColor: '#3e3e3e',
          color: '#fff',
          borderRadius: 2,
          minWidth: 120,
          height: 48,
          fontWeight: 600,
          fontSize: 16,
          '&:hover': { backgroundColor: '#222' }
        }}
        onClick={() => setActiveStep(s => Math.min(steps.length - 1, s + 1))}
      >
        Next
      </Button>
    </Box>
    
  );

  return (
    <Box sx={{ p: 3 }}>
      {renderStepper()}
      {activeStep === 0 && renderPatientSelection()}
      {activeStep === 1 && (
        <ModuleStep
          selectedModule={selectedModule}
          setSelectedModule={setSelectedModule}
        />
      )}
      {activeStep === 2 && (
        <CategoryStep
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
        />
      )}
      {/* ...other steps UI can go here... */}
      {renderNavigation()}
    </Box>
  );
};

export default TemplateModifier;
